import React from "react";
import { STRINGS } from "../../strings";
import { translate } from "../../translate";
import { AppBottomSheet } from "../Generic/AppBottomSheet";
import { AppButton } from "../Generic/AppButton";
import { AppOverlay } from "../Generic/AppOverlay";
import styled from "@emotion/styled";
import DocumentsRequireFixesIcon from "../../../public/assets/documents-require-fixes.svg?react";
import { useDocumentsRequireFixedBottomSheet } from "../../hooks/bottomsheets/useDocumentsRequireFixedBottomSheet";
import { AppTasksState } from "../../system/AppState/AppTasksState";
import { PatientTaskInstanceId } from "../../messages/ObjectIds";

interface DocumentsWithRequiredFixesBottomSheetProps {
  appTasksState: AppTasksState;
  onRequestClose: () => void;
  onClickFixDocuments: (taskInstanceId: PatientTaskInstanceId | null) => void;
}

export const DocumentsWithRequiredFixesBottomSheet = (
  props: DocumentsWithRequiredFixesBottomSheetProps
) => {
  const { isOpen, handleRequestClose, handleClickFixDocuments, documentsAmount } =
    useDocumentsRequireFixedBottomSheet({
      tasksState: props.appTasksState,
      onClickFixDocuments: props.onClickFixDocuments,
    });

  return (
    <AppOverlay>
      <DocumentsWithRequiredFixesBottomSheet.Root
        isOpen={isOpen}
        rounded={true}
        onRequestClose={handleRequestClose}
      >
        <DocumentsWithRequiredFixesBottomSheet.ImageWrapper>
          <DocumentsRequireFixesIcon />
        </DocumentsWithRequiredFixesBottomSheet.ImageWrapper>
        <DocumentsWithRequiredFixesBottomSheet.Content>
          <DocumentsWithRequiredFixesBottomSheet.Header>
            <DocumentsWithRequiredFixesBottomSheet.HeaderText>
              {documentsAmount === 1
                ? translate(STRINGS.FIX_YOUR_DOCUMENT)
                : translate(STRINGS.FIX_YOUR_DOCUMENTS)}
            </DocumentsWithRequiredFixesBottomSheet.HeaderText>

            <DocumentsWithRequiredFixesBottomSheet.SubHeader>
              <DocumentsWithRequiredFixesBottomSheet.SubHeaderText>
                {documentsAmount === 1
                  ? translate(STRINGS.FIX_DOCUMENT_DESCRIPTION)
                  : translate(STRINGS.FIX_DOCUMENTS_DESCRIPTION)}
              </DocumentsWithRequiredFixesBottomSheet.SubHeaderText>
            </DocumentsWithRequiredFixesBottomSheet.SubHeader>
          </DocumentsWithRequiredFixesBottomSheet.Header>

          <DocumentsWithRequiredFixesBottomSheet.Footer>
            <AppButton
              block
              color="primary"
              variant="filled"
              size="large"
              textColor="white"
              onClick={handleClickFixDocuments}
            >
              <DocumentsWithRequiredFixesBottomSheet.SubmitButton>
                {documentsAmount === 1
                  ? translate(STRINGS.FIX_THE_DOCUMENT)
                  : translate(STRINGS.FIX_THE_DOCUMENTS)}
              </DocumentsWithRequiredFixesBottomSheet.SubmitButton>
            </AppButton>
            <AppButton
              block
              variant="outline"
              color="black"
              size="large"
              onClick={handleRequestClose}
            >
              {translate(STRINGS.I_WILL_DO_IT_LATER)}
            </AppButton>
          </DocumentsWithRequiredFixesBottomSheet.Footer>
        </DocumentsWithRequiredFixesBottomSheet.Content>
      </DocumentsWithRequiredFixesBottomSheet.Root>
    </AppOverlay>
  );
};

DocumentsWithRequiredFixesBottomSheet.Root = styled(AppBottomSheet)`
  .AppBottomSheet_Container {
    color: var(--blue-gray);
    overflow: visible;
  }
`;

DocumentsWithRequiredFixesBottomSheet.ImageWrapper = styled.div`
  position: absolute;
  top: -30px;
  right: 0;
`;

DocumentsWithRequiredFixesBottomSheet.Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-top: 30px;
`;

DocumentsWithRequiredFixesBottomSheet.Header = styled.div`
  font-weight: 800;
  font-size: 24px;
  line-height: 24px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-inline: 10px;
`;

DocumentsWithRequiredFixesBottomSheet.HeaderText = styled.div`
  font-weight: 800;
  font-size: 24px;
  line-height: 24px;
`;

DocumentsWithRequiredFixesBottomSheet.SubHeader = styled.div`
  display: flex;
  flex-direction: row;
  gap: 10px;
`;

DocumentsWithRequiredFixesBottomSheet.SubHeaderText = styled.div`
  font-weight: 600;
  font-size: 18px;
  text-wrap: balance;
`;

DocumentsWithRequiredFixesBottomSheet.SubmitButton = styled.div`
  display: flex;
  flex-direction: row;
  gap: 4px;
  align-items: center;
`;

DocumentsWithRequiredFixesBottomSheet.Footer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-inline: 15px;
`;
